import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PrivateRoute } from '@/components/common/PrivateRoute';
import { FirmThemeProvider } from '@/components/common/FirmThemeProvider';
import { ROUTES } from '@/constants/routes';
import { HideAmountsProvider } from '@/hooks/useHideAmounts';

import { Dashboard } from '@/pages/dashboard/Dashboard';
import { Clients } from '@/pages/clients/Clients';
import { Matters } from '@/pages/matters/Matters';
import { MatterDetail } from '@/pages/matters/MatterDetail';
import { Login } from '@/pages/auth/Login';
import { TimesheetWeek } from '@/pages/timesheet/TimesheetWeek';
import { TimesheetList } from './pages/timesheet/TimesheetList';
import { TimesheetAdminReview } from './pages/timesheet/TimesheetAdminReview';
import { StatisticsLayout, DashboardTab, TeamTab, ClientsTab, MyStatsTab, MattersTab, UserDetail } from '@/pages/statistics';
import { UserReportPrintable } from '@/pages/statistics/UserReportPrintable';
import { Users } from '@/pages/users/Users';
import { Expenses } from '@/pages/expenses/Expenses';
import { UnifiedInvoicing } from '@/pages/invoices/UnifiedInvoicing';
import { InvoiceCreation } from '@/pages/invoices/InvoiceCreation';
import { InvoiceAnomalies } from '@/pages/invoices/InvoiceAnomalies';
import { ActivityLog } from '@/pages/audit/ActivityLog';
import AuditLayout from '@/pages/audit/AuditLayout';
import { ResetPassword } from '@/pages/auth/ResetPassword';
import { ActivateAccount } from '@/pages/auth/ActivateAccount';
import { ForgotPassword } from '@/pages/auth/ForgotPassword';
import { AIAssistantPage } from '@/pages/ai/AIAssistantPage';
import { Settings } from '@/pages/settings/Settings';
import { FirmSettingsPage } from '@/pages/settings/FirmSettingsPage';
import { NotFound } from '@/pages/notfound/NotFound';
import { AMLFormsPage, AMLFormPublicPage } from '@/pages/aml-forms';

// Component that returns JSON response for Microsoft identity association
function MicrosoftIdentityAssociation() {
  const data = {
    associatedApplications: [
      {
        applicationId: "45af18d1-8c5e-49dc-915f-245c60cb4b99"
      }
    ]
  };
  
  return <pre style={{ margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>;
}

function App() {
  return (
    <BrowserRouter>
      <FirmThemeProvider>
      <HideAmountsProvider>
      <Routes>
        <Route path={ROUTES.LOGIN} element={<Login />} />
        <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
        <Route path="/activate-account/:uid/:token" element={<ActivateAccount />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.HOME}
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/ai"
          element={
            <PrivateRoute>
              <AIAssistantPage />
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.TIMESHEET}
          element={
            <PrivateRoute>
              <TimesheetWeek />
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.TIMESHEET_LIST}
          element={
            <PrivateRoute>
              <TimesheetList />
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.TIMESHEET_REVIEW}
          element={
            <PrivateRoute>
              <TimesheetAdminReview />
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.COSTS}
          element={
            <PrivateRoute>
              <Expenses />
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.STATISTICS}
          element={
            <PrivateRoute>
              <StatisticsLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<DashboardTab />} />
          <Route path="team" element={<TeamTab />} />
          <Route path="clients" element={<ClientsTab />} />
          <Route path="matters" element={<MattersTab />} />
          <Route path="my-stats" element={<MyStatsTab />} />
        </Route>

        <Route
          path="/statistics/users/:userId"
          element={
            <PrivateRoute>
              <UserDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/statistics/users/:userId/report"
          element={
            <PrivateRoute>
              <UserReportPrintable />
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.USERS}
          element={
            <PrivateRoute>
              <Users />
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.CLIENT}
          element={
            <PrivateRoute>
              <Clients />
            </PrivateRoute>
          }
        />
        <Route 
          path={ROUTES.MATTERS}
          element={
            <PrivateRoute>
              <Matters />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.MATTER_DETAIL}
          element={
            <PrivateRoute>
              <MatterDetail />
            </PrivateRoute>
          }
        />
        <Route 
          path={ROUTES.INVOICES}
          element={
            <PrivateRoute>
              <UnifiedInvoicing />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CREATE_INVOICE}
          element={
            <PrivateRoute>
              <InvoiceCreation />
            </PrivateRoute>
          }
        />
        {/* Audit section — shared chrome (sidebar + tab nav) via AuditLayout. */}
        <Route
          path={ROUTES.AUDIT}
          element={
            <PrivateRoute>
              <AuditLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to={ROUTES.ACTIVITY_LOG} replace />} />
          <Route path="activity" element={<ActivityLog />} />
          <Route path="anomalies" element={<InvoiceAnomalies />} />
        </Route>
        {/* Legacy: old anomalies URL — redirect so bookmarks still land on the right page. */}
        <Route
          path={ROUTES.INVOICE_ANOMALIES}
          element={<Navigate to={ROUTES.AUDIT_ANOMALIES} replace />}
        />

        {/* Redirect legacy routes to unified invoicing */}
        <Route 
          path="/credit-notes" 
          element={
            <PrivateRoute>
              <UnifiedInvoicing />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/down-payments" 
          element={
            <PrivateRoute>
              <UnifiedInvoicing />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/create-credit-note" 
          element={
            <PrivateRoute>
              <UnifiedInvoicing />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/create-down-payment" 
          element={
            <PrivateRoute>
              <UnifiedInvoicing />
            </PrivateRoute>
          } 
        />

        <Route
          path={ROUTES.SETTINGS}
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.FIRM_SETTINGS}
          element={
            <PrivateRoute>
              <FirmSettingsPage />
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.AML_FORMS}
          element={
            <PrivateRoute>
              <AMLFormsPage />
            </PrivateRoute>
          }
        />

        {/* Public AML form route - no authentication required */}
        <Route path="/aml-forms/public/:token" element={<AMLFormPublicPage />} />

        {/* Microsoft identity association - both with and without .json extension */}
        <Route
          path="/.well-known/microsoft-identity-association.json"
          element={<MicrosoftIdentityAssociation />}
        />
        <Route
          path="/.well-known/microsoft-identity-association"
          element={<MicrosoftIdentityAssociation />}
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
      </HideAmountsProvider>
      </FirmThemeProvider>
    </BrowserRouter>
  );
}

export default App;
